/**
 * Who has access to this tenant, and what they may do (ADR-0055 D4).
 *
 * ## The one thing this page must get right
 *
 * 🔴 **IT IS NOT THE GATE.** Every rule here is enforced by the API and, beneath that, by RLS: the
 * permission check refuses a member or viewer outright, and three guards refuse the escalations a
 * role check cannot see (no self-edit, owner-is-owner-only, the last active owner stays). A disabled
 * `<select>` is a courtesy to the person using the page, never a control — anyone can call the route
 * directly, which is exactly why the control lives on the server.
 *
 * So this page's job is to make the server's answers LEGIBLE, not to pre-empt them. Where a refusal
 * is predictable it is shown before the attempt; where it is not, the server's own words are
 * rendered verbatim.
 *
 * ## Why a refusal is rendered by name
 *
 * The three refusals are each a specific, actionable thing: "promote another owner first" is a next
 * step. Collapsing them into "something went wrong" would discard the only part of the response
 * worth reading, and would leave an owner staring at a change that silently does not happen.
 */

import { useCallback, useEffect, useState, type ReactElement } from 'react';
import {
  getMembershipEvents,
  getTenantMembers,
  updateTenantMember,
  type MembershipEvent,
  type MemberRole,
  type TenantMember,
} from '../api.js';
import { useChrome } from '../chrome.js';
import { BackLink } from '../components/BackLink.js';
import { useSession } from '../useSession.js';
import { timestamp } from '../format.js';

const ROLES: readonly MemberRole[] = ['owner', 'admin', 'member', 'viewer'];

type Load =
  | { status: 'loading' }
  | { status: 'ready'; members: TenantMember[] }
  | { status: 'forbidden' }
  | { status: 'error' };

/**
 * What this caller may not change, decided BEFORE the request so the reason can be shown in place.
 *
 * ⚠️ Mirrors the server's guards deliberately and is exported for direct test, because a mirror that
 * drifts is worse than no mirror: it would disable a control the server would have allowed, and the
 * person would conclude the feature is broken. The server remains the authority — this only decides
 * what to grey out and what to say about it.
 *
 * The last-owner rule is deliberately NOT mirrored. It depends on a count that can change between
 * render and submit, so predicting it here would sometimes be wrong in the dangerous direction —
 * telling someone a change is fine when the server will refuse it. That one is left to the server
 * and rendered from its reply.
 */
export function localRefusal(
  callerRole: MemberRole | null,
  callerUserId: string | null,
  target: TenantMember,
): string | null {
  if (callerUserId !== null && target.userId === callerUserId) {
    return 'You cannot change your own role — ask another owner.';
  }
  if (callerRole !== 'owner' && (target.role === 'owner' || callerRole === null)) {
    return 'Only an owner can change an owner.';
  }
  return null;
}

/** Which roles this caller may assign. An admin may not create an owner. */
export function assignableRoles(callerRole: MemberRole | null): readonly MemberRole[] {
  return callerRole === 'owner' ? ROLES : ROLES.filter((r) => r !== 'owner');
}

/**
 * One access change, as a sentence.
 *
 * ⚠️ Reads out only what MOVED. The server records a role change with both status columns null and
 * vice versa, so rendering all four would print "status active → active" beside every role change —
 * noise that makes the real change harder to find, in the one record people consult when something
 * has gone wrong.
 */
export function describeEvent(e: MembershipEvent): string {
  const who = e.targetName ?? 'someone';
  const by = e.actorName ? ` by ${e.actorName}` : '';

  // ⚠️ BOTH halves, composed — not the first one found. `PATCH /admin/members/:userId` accepts `role`
  // and `status` in ONE body, and the writer records both movements on a single event. An earlier
  // version returned as soon as a role had moved, so a request that demoted AND disabled someone
  // rendered only the demotion — hiding the half that actually removes access, in the log kept
  // precisely to show that. Found by Bugbot.
  const parts: string[] = [];
  if (e.fromRole !== null || e.toRole !== null) {
    parts.push(`${e.fromRole ?? '—'} → ${e.toRole ?? '—'}`);
  }
  if (e.toStatus === 'disabled') parts.push('disabled');
  else if (e.toStatus === 'active') parts.push('re-enabled');
  // The capability. A non-NULL value means it MOVED — the database refuses a half-populated pair —
  // so the destination alone says which way, exactly as `toStatus` does above.
  if (e.toCanUseMcp === true) parts.push('granted MCP access');
  else if (e.toCanUseMcp === false) parts.push('had MCP access revoked');

  if (parts.length === 0) return `${who} changed${by}`;
  // A status-only change reads as a sentence rather than as a bare word: "Bo was disabled", not
  // "Bo: disabled". The role form keeps the arrow, which is the clearest way to show a transition.
  // ⚠️ The "was …" form belongs to a STATUS-only event. A capability-only event already reads as a
  // verb phrase ("granted MCP access"), so it takes the same shape without the `was`.
  if (parts.length === 1 && e.toStatus !== null) return `${who} was ${parts[0]}${by}`;
  if (parts.length === 1 && e.toCanUseMcp !== null) return `${who} ${parts[0]}${by}`;
  return `${who}: ${parts.join(', and ')}${by}`;
}

/**
 * The member list, as a PURE component.
 *
 * ⭐ Extracted from the page so it can be RENDERED in a test. The page itself is hooks and fetches —
 * `useSession`, `useEffect`, an API call — none of which run under `renderToStaticMarkup`, so the
 * markup people actually look at had no coverage at all. Every test on this page was on a pure
 * helper, and a helper returning the right string proves nothing about a table that throws.
 *
 * That matters more here than on most pages: this is the surface an owner uses to REMOVE someone's
 * access, and a crash in it is a lockout with no other route.
 */
export function MembersTable({
  members,
  callerRole,
  callerUserId,
  busy,
  onChange,
}: {
  members: readonly TenantMember[];
  callerRole: MemberRole | null;
  callerUserId: string | null;
  busy: string | null;
  onChange: (
    target: TenantMember,
    edit: { role?: MemberRole; status?: 'active' | 'disabled'; canUseMcp?: boolean },
    said: string,
  ) => void;
}) {
  return (
    <section className="card">
      <table className="table">
        <thead>
          <tr>
            <th>Person</th>
            <th>Role</th>
            <th>Status</th>
            <th>MCP access</th>
          </tr>
        </thead>
        <tbody>
          {members.map((m) => {
            const blocked = localRefusal(callerRole, callerUserId, m);
            const options = assignableRoles(callerRole);
            return (
              <tr key={m.userId}>
                <td>
                  {m.displayName ?? <span className="muted">unnamed</span>}
                  {m.userId === callerUserId && <span className="chip">you</span>}
                </td>
                <td>
                  <select
                    aria-label={`Role for ${m.displayName ?? m.userId}`}
                    value={m.role}
                    disabled={blocked !== null || busy === m.userId}
                    onChange={(e) => {
                      const role = e.target.value as MemberRole;
                      onChange(m, { role }, `${m.displayName ?? 'That member'} is now ${role}.`);
                    }}
                  >
                    {/* The current role always renders, even where this caller may not ASSIGN it —
                        otherwise an admin looking at an owner sees a blank cell instead of the truth. */}
                    {(options.includes(m.role) ? options : [m.role, ...options]).map((r) => (
                      <option key={r} value={r}>
                        {r}
                      </option>
                    ))}
                  </select>
                  {blocked && <p className="card__note">{blocked}</p>}
                </td>
                <td>
                  {m.status}
                  {/*
                    Disabling is the act that actually REMOVES access. Demoting to `viewer` still lets
                    someone read every deal in the tenant — a read-only downgrade is not a revocation.
                  */}
                  {blocked === null && m.status !== 'invited' && (
                    <button
                      type="button"
                      className="link-btn"
                      disabled={busy === m.userId}
                      onClick={() =>
                        onChange(
                          m,
                          { status: m.status === 'active' ? 'disabled' : 'active' },
                          m.status === 'active'
                            ? `${m.displayName ?? 'That member'} can no longer sign in.`
                            : `${m.displayName ?? 'That member'} has access again.`,
                        )
                      }
                    >
                      {m.status === 'active' ? 'Disable' : 'Re-enable'}
                    </button>
                  )}
                </td>
                <td>
                  {/*
                    🔴 THE DOOR ADR-0057 EXISTS TO OPEN. Before this, `can_use_mcp` was read by
                    `POST /auth/mcp-token` and written by NOTHING — unlocking MCP for the platform's
                    own owner meant SQL in a Fargate task inside the VPC. Same guards as the role
                    control, because an owner raising their OWN capability is the escalation that
                    matters most here.

                    ⚠️ Default DENY, and NOT implied by role: an owner without the grant cannot mint
                    either. The label says "not granted" rather than leaving the cell empty, because an
                    empty cell reads as "unknown" and this is a decided state.
                  */}
                  {m.canUseMcp ? 'granted' : <span className="muted">not granted</span>}
                  {blocked === null && (
                    <button
                      type="button"
                      className="link-btn"
                      disabled={busy === m.userId}
                      onClick={() =>
                        onChange(
                          m,
                          { canUseMcp: !m.canUseMcp },
                          m.canUseMcp
                            ? `${m.displayName ?? 'That member'} can no longer mint an MCP token.`
                            : `${m.displayName ?? 'That member'} can now mint an MCP token.`,
                        )
                      }
                    >
                      {m.canUseMcp ? 'Revoke' : 'Grant'}
                    </button>
                  )}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </section>
  );
}

/**
 * The access history, as a PURE component.
 *
 * 🔴 The reader `membership_events` did not have. Rendered under the list because it answers the
 * question people arrive with once the list surprises them: "who changed this, and when?"
 */
export function AccessHistory({ events }: { events: readonly MembershipEvent[] }) {
  return (
    <section className="card">
      <h3 className="card__title">Recent access changes</h3>
      {events.length === 0 ? (
        <p className="card__note">
          Nothing recorded yet. Changes made from this page are logged here from now on.
        </p>
      ) : (
        <ul className="stdq__list">
          {events.map((e) => (
            <li key={e.id}>
              {describeEvent(e)} <span className="muted">{timestamp(e.createdAt)}</span>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

export function AdminMembers() {
  const session = useSession();
  // Supplied by the application root — this page does not decide which product it belongs to.
  const Chrome = useChrome();
  const [load, setLoad] = useState<Load>({ status: 'loading' });
  const [busy, setBusy] = useState<string | null>(null);
  const [notice, setNotice] = useState<{ tone: 'refused' | 'done'; text: string } | null>(null);
  const [events, setEvents] = useState<MembershipEvent[]>([]);

  const claims = session.status === 'authed' ? session.claims : null;
  const callerRole = (claims?.['role'] as MemberRole | undefined) ?? null;
  const callerUserId = (claims?.['userId'] as string | undefined) ?? null;

  const refresh = useCallback(async () => {
    const res = await getTenantMembers();
    if (res.kind === 'forbidden') return setLoad({ status: 'forbidden' });
    if (res.kind === 'error') return setLoad({ status: 'error' });
    setLoad({ status: 'ready', members: res.members });
    // Best-effort beside the list: a history that fails to load must not take the page with it.
    setEvents(await getMembershipEvents());
  }, []);

  useEffect(() => {
    if (session.status !== 'authed') return;
    void refresh();
  }, [session.status, refresh]);

  async function change(
    target: TenantMember,
    edit: { role?: MemberRole; status?: 'active' | 'disabled'; canUseMcp?: boolean },
    said: string,
  ): Promise<void> {
    setBusy(target.userId);
    setNotice(null);
    const res = await updateTenantMember(target.userId, edit);
    setBusy(null);
    if (res.kind === 'refused') {
      // The server's own sentence, not a paraphrase — it names the next step.
      setNotice({ tone: 'refused', text: res.message });
      return;
    }
    if (res.kind !== 'ok') {
      setNotice({
        tone: 'refused',
        text:
          res.kind === 'forbidden'
            ? 'You do not have permission to change memberships.'
            : 'That change could not be saved. Nothing was altered.',
      });
      return;
    }
    setNotice({ tone: 'done', text: said });
    // Re-read rather than patching local state: the server is the record, and a re-read is the only
    // way this list reflects a change someone else made in the meantime.
    await refresh();
  }

  if (session.status !== 'authed' || load.status === 'loading') {
    return <div className="app__loading">Loading…</div>;
  }

  /** One shell for every state, so a refusal is not a differently-shaped page. */
  const shell = (body: ReactElement) => (
    <div className="app">
      {Chrome !== null && <Chrome claims={session.claims} />}
      <main className="app__main">
        <div className="app__crumbs">
          <BackLink fallback="/" label="Back" />
        </div>
        <div className="app__intro">
          <h1>Access</h1>
          <p className="sub">
            Who can reach this tenant, and what they may do. A <strong>viewer</strong> reads and
            does nothing else — it is the role a client holds.
          </p>
        </div>
        {body}
      </main>
    </div>
  );

  if (load.status === 'forbidden') {
    return shell(
      <div className="notice">
        Only an owner or admin can see who has access. That is deliberate — a member list names
        people.
      </div>,
    );
  }

  if (load.status === 'error') {
    return shell(
      <div className="notice">
        We couldn’t load the member list just now. Nothing has been changed. Refresh to try again.
      </div>,
    );
  }

  return shell(
    <>
      {notice && <div className="notice">{notice.text}</div>}
      <MembersTable
        members={load.members}
        callerRole={callerRole}
        callerUserId={callerUserId}
        busy={busy}
        // `change` is async and the prop is void-returning by design: the table fires and forgets,
        // and the page owns the await. Discarded explicitly rather than implicitly.
        onChange={(target, edit, said) => {
          void change(target, edit, said);
        }}
      />
      <AccessHistory events={events} />
    </>,
  );
}
